import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight, ChevronUp, Search } from 'lucide-react';

import teamBadgePlaceholder from '../../../assets/images/team_badge_placeholder.svg';
import { ReactComponent as WinsIcon } from '../../../assets/images/icons/wins-icon.svg';
import { ReactComponent as WinPercentIcon } from '../../../assets/images/icons/win-percent.svg';
import { ReactComponent as GoalsScoredIcon } from '../../../assets/images/icons/goals-scored.svg';
import { ReactComponent as CleanSheetsIcon } from '../../../assets/images/icons/clean-sheets.svg';
import { ReactComponent as YellowIcon } from '../../../assets/images/icons/yellow-card.svg';
import { ReactComponent as RedIcon } from '../../../assets/images/icons/red-card.svg';
import { ReactComponent as ColumnSettingsIcon } from '../../../assets/images/icons/column-settings.svg';
import Checkbox from '../../../components/Checkbox/Checkbox';
import EmptyState from '../../../components/Statistics/EmptyState';
import ClubbieLoader from '../../../components/Loader/ClubbieLoader';
import { useIsPublic } from '../../../context/PublicContext';
import { getCategoryStats } from '../../../redux/actions/tournamentActions';
import { noAuthApi } from '../../../utils/axios';
import { config } from '../../../utils/config';

/**
 * StatisticsV2 - Modern JSX component with Tailwind CSS
 * Displays tournament statistics with team rankings, top teams, and key statistics table
 */
const StatisticsV2 = () => {
  const isPublic = useIsPublic();
  const { selectedTournament, selectedCategory } = useSelector(
    (state) => state.tournament
  );

  const [seasonStats, setSeasonStats] = useState({ topTeams: {}, teamRankings: [] });
  const [loading, setLoading] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('winPercent');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeStatCat, setActiveStatCat] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showRowsDropdown, setShowRowsDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const rowsDropdownRef = useRef(null);

  const ROWS_OPTIONS = [5, 10, 25, 50];

  // Initialize to first category when tournament loads
  useEffect(() => {
    if (selectedTournament?.categories?.length > 0 && !activeStatCat) {
      setActiveStatCat(selectedTournament.categories[0].label);
    }
  }, [selectedTournament, activeStatCat]);

  // Determine which category ID to fetch stats for
  const activeCategoryId = useMemo(() => {
    const cat = selectedTournament?.categories?.find(c => c.label === activeStatCat);
    return cat?._id || selectedCategory?._id;
  }, [activeStatCat, selectedCategory, selectedTournament]);

  const filterOptions = [
    { id: 'winPercent', label: 'Win %', icon: WinPercentIcon },
    { id: 'wins', label: 'Wins', icon: WinsIcon },
    { id: 'goalsScored', label: 'Goals Scored', icon: GoalsScoredIcon },
    { id: 'cleanSheets', label: 'Clean Sheets (CS)', icon: CleanSheetsIcon },
    { id: 'yellowCards', label: 'Yellow Cards', icon: YellowIcon },
    { id: 'redCards', label: 'Red Cards', icon: RedIcon },
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (rowsDropdownRef.current && !rowsDropdownRef.current.contains(e.target))
        setShowRowsDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset page when search changes
  useEffect(() => { setCurrentPage(1); }, [teamSearch]);
  useEffect(() => { setCurrentPage(1); }, [rowsPerPage]);

  // Fetch season stats
  useEffect(() => {
    if (!selectedTournament || !activeCategoryId) return;

    setLoading(true);

    if (isPublic) {
      noAuthApi
        .get(`/${config.API_PATH}/tournaments/${selectedTournament.id}/categories/${activeCategoryId}/stats`)
        .then(({ data }) => {
          setSeasonStats(data);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      getCategoryStats(
        selectedTournament.id,
        activeCategoryId,
        (data) => {
          setSeasonStats(data);
          setLoading(false);
        },
        (error) => {
          setLoading(false);
        }
      );
    }
  }, [selectedTournament, activeCategoryId, isPublic]);

  // Process table data
  const tableData = useMemo(() => {
    let rows =
      seasonStats.teamRankings?.map((team) => ({
        id: team.teamId,
        team: team.title,
        rank: team.rank,
        matches: `${team.matches || 0}`,
        wins: `${team.wins}`,
        draws: `${team.draws}`,
        losses: `${team.losses}`,
        latestMatches: team?.lmResults,
        winPercentage: `${team.winPercentage}%`,
        goalsScored: `${team.goalsScored || 0}`,
        cleanSheets: `${team.cleanSheets || 0}`,
        yellowCards: `${team.yellowCards || 0}`,
        redCards: `${team.redCards || 0}`,
        crest: team.crest,
      })) || [];

    if (teamSearch.trim()) {
      rows = rows.filter((row) =>
        row.team.toLowerCase().includes(teamSearch.toLowerCase())
      );
    }

    const key = selectedFilter === 'winPercent' ? 'winPercentage' : selectedFilter;
    rows.sort((a, b) => parseFloat(b[key]) - parseFloat(a[key]));

    return rows;
  }, [seasonStats.teamRankings, teamSearch, selectedFilter]);

  // Pagination
  const totalRows = tableData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
  const paginatedData = tableData.slice(startIndex, endIndex);

  // Selected stat column metadata
  const statKey = selectedFilter === 'winPercent' ? 'winPercentage' : selectedFilter;
  const statLabel = filterOptions.find((f) => f.id === selectedFilter)?.label || 'Win %';

  // Check if we have any statistics data
  const hasData = useMemo(() => {
    return (
      tableData.length > 0 ||
      seasonStats?.topTeams?.mostGoals?.value > 0 ||
      seasonStats?.topTeams?.bestGoalDifference?.value > 0 ||
      seasonStats?.topTeams?.mostCleanSheets?.value > 0
    );
  }, [tableData, seasonStats]);

  const isEmpty = !hasData && !loading;

  return (
    <div className={`p-3 sm:p-[18px] lg:px-8 flex flex-col gap-5 ${isEmpty ? "h-full" : ""}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-[22px] font-normal text-[#FEFCEB]">Statistics</h1>
        <div className="flex items-center gap-3 opacity-0">
            <div
              className="px-4 py-2 text-[15px] font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors"
            >
              dummy
            </div>
            <button
              className="bg-[#00E7FF] hover:opacity-50 text-[#021529] text-[15px] font-medium px-4 py-2 rounded-full transition-all active:scale-95"
            >
              dummy
            </button>
        </div>
      </div>

        {/* Category Tabs */}
      {selectedTournament?.categories?.length > 0 && (
        <div className="flex items-center gap-3">
          {selectedTournament.categories.map((cat) => (
            <button
              key={cat._id}
              className={`flex h-[40px] justify-center items-center gap-2 rounded-full px-4 text-sm font-medium transition-all
                ${activeStatCat === cat.label
                  ? 'bg-[#001A2A] text-[#00E7FF]'
                  : 'text-app-text-muted hover:text-app-accent'}`}
              onClick={() => setActiveStatCat(cat.label)}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
          <div className="flex justify-center items-center py-20">
            <ClubbieLoader />
          </div>
        ) : !hasData ? (
          /* Empty State */
          <EmptyState />
        ) : (
          <>
            {/* Top Teams Section */}
            <div className="border border-app-border rounded-2xl overflow-hidden">
              <div className="bg-app-surface border-b border-app-border px-4 flex items-center py-4">
                <p className="text-xs text-app-text-secondary">Top teams</p>
              </div>
              <div className="grid grid-cols-3 divide-x divide-app-border px-5 py-4">
                {[
                  { data: seasonStats?.topTeams?.mostGoals, label: 'Most goals scored', defaultColor: 'bg-[#D12953]' },
                  { data: seasonStats?.topTeams?.bestGoalDifference, label: 'Most goals difference', defaultColor: 'bg-[#10A760]' },
                  { data: seasonStats?.topTeams?.mostCleanSheets, label: 'Most clean sheets', defaultColor: 'bg-[#FA5F28]' },
                ].map(({ data, label, defaultColor }, idx) => (
                  <div key={idx} className={`flex items-center justify-between ${idx > 0 ? 'pl-6' : ''} ${idx < 2 ? 'pr-6' : ''}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0`}>
                        <img
                          src={data?.crest || teamBadgePlaceholder}
                          alt={data?.title || 'team'}
                          className="w-full h-full object-contain rounded-full"
                          onError={(e) => { e.target.src = teamBadgePlaceholder; }}
                        />
                      </div>
                      <div className="flex flex-col items-start justify-center">
                        <p className="text-sm text-app-text font-medium line-clamp-1">{data?.title || 'N/A'}</p>
                        <p className="text-xs text-app-text-secondary">{label}</p>
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-app-text ml-2">{data?.value ?? 0}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Key Statistics Section */}
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-app-text mb-3">Key statistics</h2>
              <div className="flex items-center gap-3 mb-4 flex-wrap">

                {/* Search Input */}
                <div className="relative flex-1 min-w-[180px] max-w-xs">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-app-text-secondary">
                    <Search size={18} />
                  </div>
                  <input
                    type="text"
                    value={teamSearch}
                    placeholder="Search team"
                    className="w-full bg-app-surface border border-app-surface rounded-3xl pl-9 pr-4 py-2.5 text-[14px] text-[#FEFCEB] placeholder-app-text-secondary focus:border-[#00E7FF] outline-none transition-colors"
                    onChange={(e) => setTeamSearch(e.target.value)}
                  />
                </div>

                {/* Column Settings Dropdown */}
                <div className="relative ml-auto" ref={dropdownRef}>
                  <button
                    className="flex items-center gap-2 px-4 py-2 bg-[#082441] hover:opacity-50 text-app-text text-[15px] font-medium rounded-full transition-all active:scale-95"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    <ColumnSettingsIcon className="w-4 h-4" />
                    <span className="text-sm font-medium">Column settings</span>
                  </button>

                  {dropdownOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-[#000F1F] border border-[#192634] rounded-2xl shadow-xl z-50 overflow-hidden">
                      {filterOptions.map((filter) => {
                        const Icon = filter.icon;
                        const isSelected = selectedFilter === filter.id;
                        return (
                          <button
                            key={filter.id}
                            className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors border-b border-[#192634] last:border-b-0 flex items-center gap-3 ${
                              isSelected ? 'text-app-accent' : 'text-[#FEFCEB] hover:bg-[#0D1B29]'
                            }`}
                            onClick={() => {
                              setSelectedFilter(filter.id);
                              setDropdownOpen(false);
                            }}
                          >
                            <Checkbox checked={isSelected} onChange={() => {}} />
                            <Icon className="w-4 h-4 text-[#B2B5AD] shrink-0" />
                            {filter.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Statistics Table */}
              <div className="w-full overflow-x-auto rounded-2xl border border-app-hover">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-app-surface border-b border-app-hover">
                      <th className="p-4 text-sm font-medium text-app-text-secondary w-1/3">Team</th>
                      <th className="p-4 text-sm font-medium text-app-text-secondary w-1/4">Form</th>
                      <th className="p-4 text-sm font-medium text-app-text-secondary w-1/3">{statLabel}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.length === 0 && (
                      <tr>
                        <td colSpan={3} className="py-10 text-center text-sm text-app-text-secondary">
                          {teamSearch ? `No results for "${teamSearch}"` : 'No data available'}
                        </td>
                      </tr>
                    )}
                    {paginatedData.map((row) => (
                      <tr key={row.id} className="border-b border-app-hover transition-colors group">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <img
                              src={row.crest || teamBadgePlaceholder}
                              alt="team"
                              className="w-6 h-6 rounded-full object-cover bg-gray-800"
                              onError={(e) => { e.target.src = teamBadgePlaceholder; }}
                            />
                            <span className="text-sm font-medium text-app-text">{row.team}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 justify-start">
                            {row.latestMatches?.slice(-5).map((result, idx) => {
                              if (!result) return null;
                              const r = result.toUpperCase();
                              const color = r === 'W' ? '#4AED88' : r === 'D' ? '#FFD700' : r === 'L' ? '#FF7B7B' : '#7F8585';
                              return (
                                <div
                                  key={idx}
                                  className="w-4 h-4 rounded-full border border-app-bg"
                                  style={{ backgroundColor: color, marginLeft: idx > 0 ? -7 : 0 }}
                                />
                              );
                            })}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-app-text">{row[statKey]}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalRows > 0 && (
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-app-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={18} className="text-app-text" />
                    </button>
                    <button
                      onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-app-card disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight size={18} className="text-app-text" />
                    </button>
                  </div>
                  <span className="text-sm text-app-text-muted">
                    Show {startIndex + 1}–{endIndex} of {totalRows} {totalRows === 1 ? 'team' : 'teams'}
                  </span>
                  <div className="flex items-center gap-2 text-sm text-app-text-muted">
                    <span>Rows per page</span>
                    <div className="relative" ref={rowsDropdownRef}>
                      <button
                        onClick={() => setShowRowsDropdown(!showRowsDropdown)}
                        className="flex items-center gap-1 bg-app-surface px-3 py-1.5 rounded-2xl text-app-text font-medium"
                      >
                        {rowsPerPage} Row
                        <ChevronUp
                          size={16}
                          className={`transition-transform duration-200 shrink-0 ${showRowsDropdown ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {showRowsDropdown && (
                        <div className="absolute right-0 bottom-full mb-2 w-24 bg-[#000F1F] border border-app-hover rounded-xl shadow-xl z-50 overflow-hidden">
                          {ROWS_OPTIONS.map((option) => (
                            <button
                              key={option}
                              onClick={() => { setRowsPerPage(option); setShowRowsDropdown(false); }}
                              className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors border-b border-app-hover last:border-b-0 ${
                                rowsPerPage === option ? 'text-app-accent' : 'text-app-text hover:bg-app-hover'
                              }`}
                            >
                              {option} Row
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
    </div>
  );
};

export default StatisticsV2;
